import type { PrimitivePermissionType } from '@repo/api-codegen';
import { UserRole } from '@repo/common/enums/UserRole';
import type { IconNames } from '@repo/foundations';
import type { IWorkspace } from '../../api';
import { CreateTab } from '../../pages/IntegrationsBrowsePage/CreateTab';
import type { FeatureCondition } from '../../utils/featureFlags/complexFeatureUtils';
import {
	and,
	featureAccess as createFeatureAccess,
	featureFlag as createFeatureFlag,
	not,
	or,
} from '../../utils/featureFlags/complexFeatureUtils';
import { AuditLogWrapper } from '../AuditLog';
import { AIToolsSettings } from './AIToolsSettings';
import { AiTokenUsageSettings } from './AiTokenUsageSettings';
import { ApiSettings } from './ApiSettingsPage/ApiSettingsPage';
import { AutomationSettings } from './AutomationSettings';
import { BillingSettings } from './BillingSettings';
import CatalogDefaultsSettings from './CatalogDefaultsSettings';
import { ChromeExtensionSettings } from './ChromeExtensionSettings/ChromeExtensionSettings';
import { DarSettings } from './DarSettings';
import { DataSettings } from './DataSettings';
import { ExtensionSettings } from './ExtensionSettings/ExtensionSettings';
import { ImportAndExportSettings } from './ImportAndExportSettings/ImportAndExportSettings';
import { MemberSettings } from './MemberSettings/MemberSettings';
import { MonitoringSettings } from './MonitoringSettings';
import { NotificationSettings } from './NotificationSettings';
import { PoliciesSettings } from './PoliciesSettings';
import { Preferences } from './Preferences';
import { ProfileSettings } from './ProfileSettings';
import { PublishSettings } from './PublishSettings';
import { QualityScoreSettings } from './QualityScoreSettings';
import { QuestionsSettings } from './QuestionsSettings';
import { SecuritySettings } from './SecuritySettings';
import { TagSettings } from './TagSettings/TagSettings';
import { TemplateSettings } from './TemplateSettings/TemplateSettings';
import { UserLevelCredentialsSettings } from './UserLevelCredentialsSettings';
import { WorkspaceSettings } from './WorkspaceSettings';

export interface TabType {
	name: string;
	id: string;
	title?: string;
	sidebarTitle?: string;
	description?: string;
	documentationLink?: string;
	allowedRoles?: UserRole[];
	v2AllowedPermissions?: PrimitivePermissionType[];
	v2DeniedPermissions?: PrimitivePermissionType[];
	featureCondition?: FeatureCondition;
	component: React.JSXElementConstructor<unknown>;
	iconName: IconNames;
	isBeta?: boolean;
	betaExpiresAt?: Date;
	isNew?: boolean;
	newExpiresAt?: Date;
}

export const getNotificationTabName = (subtab?: string) => {
	if (subtab === 'app') return 'App';
	if (subtab === 'email') return 'Email';
	if (subtab === 'slack') return 'Slack';
	return 'Notifications';
};

export const profileTabs = (subtab?: string): Array<TabType> => [
	{
		name: 'Preferences',
		id: 'preferences',
		description: 'Configure your preferences and settings',
		component: Preferences,
		iconName: 'adjustmentsHorizontal',
	},
	{
		name: 'Profile',
		id: 'profile',
		description: 'Configure your personal account settings and preferences',
		component: ProfileSettings,
		iconName: 'user',
	},
	{
		name: 'Connected accounts',
		id: 'connected-accounts',
		description:
			'Manage your personal credentials for integrations that require user-level authentication',
		component: UserLevelCredentialsSettings,
		iconName: 'link',
		featureCondition: createFeatureFlag('userLevelCredentials'),
	},
	{
		name: getNotificationTabName(subtab),
		sidebarTitle: 'Notifications',
		id: 'notifications',
		description: 'Configure your notification preferences and alert settings',
		component: NotificationSettings,
		iconName: 'bell',
		featureCondition: createFeatureAccess('secodaCoreAccess'),
	},
	{
		name: 'Google Chrome extension',
		id: 'chrome-extension',
		description:
			'Access your data directly from Chrome with the browser extension',
		component: ChromeExtensionSettings,
		iconName: 'brandChrome',
		documentationLink: 'https://docs.secoda.com/extensions/chrome-extension',
		featureCondition: createFeatureAccess('secodaCoreAccess'),
	},
];

export const featureTabs = (): Array<TabType> => [
	{
		name: 'AI',
		id: 'ai',
		description: 'Configure AI settings',
		documentationLink: 'https://docs.secoda.com/features/ai-assistant',
		allowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2AllowedPermissions: ['SecodaAI.Write'],
		component: AIToolsSettings,
		iconName: 'sparkles',
	},
	{
		name: 'Quality score',
		id: 'quality-score',
		description: 'Configure quality score settings',
		documentationLink: 'https://docs.secoda.com/features/data-quality-score',
		allowedRoles: [UserRole.ADMIN],
		v2AllowedPermissions: ['DQS.Write'],
		component: QualityScoreSettings,
		iconName: 'progressCheck',
		featureCondition: createFeatureAccess('secodaCoreAccess'),
	},
	{
		name: 'Access requests',
		id: 'data-access-requests',
		description: 'Configure data access request settings',
		documentationLink: 'https://docs.secoda.com/features/access-requests',
		allowedRoles: [UserRole.ADMIN],
		v2AllowedPermissions: ['Integrations.Create', 'Integrations.Update'],
		component: DarSettings,
		iconName: 'lockOpen',
		isBeta: true,
		betaExpiresAt: new Date('2025-07-01'),
		featureCondition: createFeatureAccess('secodaCoreAccess'),
		isNew: true,
		newExpiresAt: new Date('2025-08-01'),
	},
	{
		name: 'Policies',
		id: 'policies',
		description: 'Configure data access request settings',
		documentationLink: 'https://docs.secoda.com/features/policies',
		allowedRoles: [UserRole.ADMIN],
		v2AllowedPermissions: ['Policies.Create'],
		component: PoliciesSettings,
		iconName: 'fileCheck',
		featureCondition: and(
			createFeatureFlag('policies'),
			createFeatureAccess('secodaCoreAccess')
		),
		isBeta: true,
		betaExpiresAt: new Date('2025-07-01'),
	},
	{
		name: 'Questions',
		id: 'questions',
		description: 'Configure questions settings',
		documentationLink:
			'https://docs.secoda.com/features/ask-questions-in-secoda',
		allowedRoles: [UserRole.ADMIN],
		v2AllowedPermissions: ['Workspace.Write'],
		component: QuestionsSettings,
		iconName: 'messageCircleQuestion',
		featureCondition: createFeatureAccess('secodaCoreAccess'),
		isNew: true,
		newExpiresAt: new Date('2025-08-01'),
	},
	{
		name: 'Automations',
		id: 'automations',
		description: 'Configure automation settings',
		documentationLink: 'https://docs.secoda.com/features/automations',
		allowedRoles: [UserRole.ADMIN],
		v2AllowedPermissions: ['Automations.Create'],
		component: AutomationSettings,
		iconName: 'bolt',
		featureCondition: createFeatureAccess('secodaCoreAccess'),
	},
	{
		name: 'Tags',
		id: 'tags',
		description: 'Configure tag settings',
		v2AllowedPermissions: ['Tags.Create', 'Tags.Delete', 'Tags.Update'],
		allowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		component: TagSettings,
		iconName: 'tags',
		documentationLink:
			'https://docs.secoda.com/resource-and-metadata-management/tags',
	},
	{
		name: 'Templates',
		id: 'templates',
		description: 'Configure templates settings',
		allowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2AllowedPermissions: ['Workspace.Write'],
		component: TemplateSettings,
		iconName: 'fileDescription',
		documentationLink:
			'https://docs.secoda.com/resource-and-metadata-management/add-documentation/templates',
	},
	{
		name: 'Custom properties',
		id: 'properties',
		description:
			'Define and manage catalog properties and metadata configuration',
		allowedRoles: [UserRole.ADMIN],
		v2AllowedPermissions: ['Properties.Update', 'Properties.Create'],
		component: CatalogDefaultsSettings,
		iconName: 'list',
		documentationLink:
			'https://docs.secoda.com/resource-and-metadata-management/adding-custom-properties',
		featureCondition: createFeatureAccess('secodaCoreAccess'),
	},
	{
		name: 'Publishing',
		id: 'publishing',
		description: 'Configure publishing settings',
		v2AllowedPermissions: ['Workspace.Write'],
		allowedRoles: [UserRole.ADMIN],
		component: PublishSettings,
		iconName: 'bookUpload',
		documentationLink: 'https://docs.secoda.com/features/publishing',
		featureCondition: createFeatureAccess('secodaCoreAccess'),
	},
	{
		name: 'PII scanning',
		id: 'pii-scanning',
		description: 'Configure PII scanning settings',
		allowedRoles: [UserRole.ADMIN],
		v2AllowedPermissions: ['Workspace.Write'],
		component: DataSettings,
		iconName: 'shieldSearch',
		documentationLink:
			'https://docs.secoda.com/resource-and-metadata-management/tags/auto-pii-tagging',
		featureCondition: createFeatureAccess('secodaCoreAccess'),
	},
	{
		name: 'Monitors',
		id: 'monitors',
		description: 'Configure monitoring and access request settings',
		documentationLink: 'https://docs.secoda.com/features/monitoring',
		allowedRoles: [UserRole.ADMIN],
		v2AllowedPermissions: ['Workspace.Write'],
		component: MonitoringSettings,
		iconName: 'activity',
		featureCondition: createFeatureAccess('secodaCoreAccess'),
	},
];

export const workspaceTabs = (workspace: IWorkspace): Array<TabType> => [
	{
		name: 'Workspace',
		id: 'workspace',
		sidebarTitle: 'Workspace',
		title: 'Workspace settings',
		description:
			'Configure core workspace settings and organization preferences',
		allowedRoles: [UserRole.ADMIN],
		v2AllowedPermissions: ['Workspace.Write'],
		component: WorkspaceSettings,
		iconName: 'building',
	},
	{
		name: workspace.migrated_permission_v2
			? 'Members and permissions'
			: 'Members',
		id: 'members',
		description:
			'Manage workspace members, configure user groups, and customize role-based permissions',
		allowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2AllowedPermissions: [
			'Groups.Create',
			'Groups.Delete',
			'Groups.Update',
			'Users.Create',
			'Users.Delete',
			'Users.Update',
			'Roles.Create',
			'Roles.Delete',
			'Roles.Update',
		],
		component: MemberSettings,
		iconName: 'users',
		documentationLink: 'https://docs.secoda.com/user-management',
	},
	{
		name: 'Security',
		id: 'security',
		title: 'Security',
		sidebarTitle: 'Security',
		v2AllowedPermissions: ['Workspace.Write'],
		description:
			'Security settings for your workspace. SAML is only available on the Enterprise plan',
		documentationLink: 'https://docs.secoda.com/saml',
		allowedRoles: [UserRole.ADMIN],
		component: SecuritySettings,
		iconName: 'shield',
	},
	{
		name: 'Import',
		id: 'import',
		title: 'Import and export',
		sidebarTitle: 'Import and export',
		description: 'Manage data import/export operations and bulk data transfers',
		v2AllowedPermissions: ['ImportExports.Write', 'ImportExports.Read'],
		documentationLink:
			'https://docs.secoda.com/features/data-management/import-and-export-data',
		allowedRoles: [UserRole.ADMIN],
		component: ImportAndExportSettings,
		iconName: 'arrowsUpDown',
		featureCondition: createFeatureAccess('secodaCoreAccess'),
	},
	{
		name: 'Billing',
		id: 'billing',
		v2AllowedPermissions: ['Billing.Write', 'Billing.Read'],
		description:
			'Workspace billing history and additional details will show up here',
		allowedRoles: [UserRole.ADMIN],
		component: BillingSettings,
		iconName: 'creditCard',
	},
	{
		name: 'AI usage',
		id: 'ai-token-usage',
		description: 'Monitor AI usage and associated costs',
		allowedRoles: [UserRole.ADMIN],
		v2AllowedPermissions: ['SecodaAI.Read'],
		component: AiTokenUsageSettings,
		iconName: 'chartLine',
		featureCondition: or(
			createFeatureFlag('aiTokens'),
			not(createFeatureAccess('secodaCoreAccess'))
		),
	},
	{
		name: 'API',
		id: 'api',
		title: 'API',
		description:
			'Manage API credentials and access tokens for programmatic integration',
		v2AllowedPermissions: ['APIKeys.Read'],
		documentationLink: 'https://docs.secoda.co/api',
		allowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		component: ApiSettings,
		iconName: 'key',
		featureCondition: createFeatureAccess('secodaCoreAccess'),
	},
	{
		name: 'Audit log',
		id: 'audit-log',
		description: 'Review detailed workspace activity and security audit trails',
		allowedRoles: [UserRole.ADMIN],
		v2AllowedPermissions: ['Workspace.Write'],
		component: AuditLogWrapper,
		iconName: 'reportSearch',
		isBeta: true,
		betaExpiresAt: new Date('2025-07-01'),
		featureCondition: createFeatureAccess('secodaCoreAccess'),
	},
	{
		name: 'Extensions',
		id: 'extensions',
		v2AllowedPermissions: ['Integrations.Create'],
		description:
			'Manage your productivity, communication, and collaboration extensions',
		documentationLink: 'https://docs.secoda.com/extensions',
		component: ExtensionSettings,
		allowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		iconName: 'layoutGrid',
	},
	{
		name: 'Marketplace',
		id: 'marketplace-integrations',
		v2AllowedPermissions: ['Integrations.Create'],
		description: 'Develop and publish custom integrations using the Secoda SDK',
		documentationLink:
			'https://docs.secoda.com/integrations/custom-integrations-and-marketplace/secoda-sdk-custom-integration',
		allowedRoles: [UserRole.ADMIN],
		component: CreateTab,
		iconName: 'layoutGridAdd',
		featureCondition: createFeatureAccess('secodaCoreAccess'),
	},
];
